import { BadgeDesign, DEFAULT_BADGE_DESIGN } from '../../../../../common/badges/badge-svg';
import { AdminConsoleAchievement } from './__generated__/AdminConsoleAchievement';

export type AchievementMetric = 'hours' | 'years_of_service' | 'sessions_attended' | 'report_field_total' | 'manual';

interface MetricInfo {
  label: string;
  unit: string;
  badgeUnit: string;
}

const METRIC_LIST: [AchievementMetric, MetricInfo][] = [
  ['hours', { label: 'Hours', unit: 'hours', badgeUnit: 'hours' }],
  ['years_of_service', { label: 'Years of service', unit: 'years', badgeUnit: 'years' }],
  ['sessions_attended', { label: 'Sessions attended', unit: 'sessions', badgeUnit: 'shifts' }],
  ['report_field_total', { label: 'Report field total', unit: '', badgeUnit: '' }],
  ['manual', { label: 'Manual', unit: '', badgeUnit: '' }],
];

export const METRICS = METRIC_LIST.reduce(
  (all, [metric, info]) => ({ ...all, [metric]: info }),
  {} as Record<AchievementMetric, MetricInfo>
);

export const METRIC_OPTIONS = METRIC_LIST.map(([metric]) => metric);

export interface NumericReportField {
  fieldId: string;
  name: string;
  reportTypeName: string;
}

export interface LevelDraft {
  key: string;
  achievementLevelId: string | null;
  name: string;
  threshold: string;
  certificateTemplateId: string;
  badgeDesign: BadgeDesign | null;
  liveFrom: string | null;
  originalDesign: string | null;
  hasImage: boolean;
}

export interface AchievementDraft {
  achievementId: string | null;
  name: string;
  description: string;
  metric: AchievementMetric;
  reportFieldId: string;
  isEnabled: boolean;
  isDiscoverable: boolean;
  showProgress: boolean;
  customMessage: string;
  levels: LevelDraft[];
}

const LEVEL_COLOURS = ['bronze', 'silver', 'gold', 'platinum', 'rosegold'];

let keySeed = 0;
const nextKey = () => `level-${keySeed++}`;

export const isThresholdBased = (metric: AchievementMetric) => metric !== 'manual';

export const parseBadgeDesign = (json: string | null): BadgeDesign | null => {
  if (!json) return null;
  try {
    return { ...DEFAULT_BADGE_DESIGN, ...JSON.parse(json) };
  } catch (e) {
    return null;
  }
};

export const defaultBadge = (metric: AchievementMetric, index: number, threshold: string): BadgeDesign => ({
  ...DEFAULT_BADGE_DESIGN,
  centre: isThresholdBased(metric) ? 'number' : 'icon',
  value: threshold.replace(/\D/g, '').slice(0, 4),
  unit: METRICS[metric].badgeUnit,
  colour: LEVEL_COLOURS[index % LEVEL_COLOURS.length],
});

export const newLevel = (): LevelDraft => ({
  key: nextKey(),
  achievementLevelId: null,
  name: '',
  threshold: '',
  certificateTemplateId: '',
  badgeDesign: null,
  liveFrom: null,
  originalDesign: null,
  hasImage: false,
});

export const emptyDraft = (): AchievementDraft => ({
  achievementId: null,
  name: '',
  description: '',
  metric: 'hours',
  reportFieldId: '',
  isEnabled: true,
  isDiscoverable: true,
  showProgress: false,
  customMessage: '',
  levels: [newLevel()],
});

export const draftFromAchievement = (achievement: AdminConsoleAchievement): AchievementDraft => ({
  achievementId: achievement.achievementId,
  name: achievement.name,
  description: achievement.description ?? '',
  metric: achievement.metric as AchievementMetric,
  reportFieldId: achievement.reportFieldId ?? '',
  isEnabled: achievement.isEnabled,
  isDiscoverable: achievement.isDiscoverable,
  showProgress: achievement.showProgress,
  customMessage: achievement.customMessage ?? '',
  levels: achievement.levels.map((level) => ({
    key: nextKey(),
    achievementLevelId: level.achievementLevelId,
    name: level.name,
    threshold: level.threshold === null ? '' : String(level.threshold),
    certificateTemplateId: level.certificateTemplate?.certificateTemplateId ?? '',
    badgeDesign: parseBadgeDesign(level.badgeDesign),
    liveFrom: level.liveFrom,
    originalDesign: level.badgeDesign ? JSON.stringify(parseBadgeDesign(level.badgeDesign)) : null,
    hasImage: Boolean(level.badgeImageUrl),
  })),
});

export const validateDraft = (draft: AchievementDraft): string | null => {
  if (!draft.name.trim()) return 'Give the achievement a name';
  if (draft.metric === 'report_field_total' && !draft.reportFieldId) return 'Choose a report field';
  if (!draft.levels.length) return 'Add at least one level';
  if (draft.levels.some((level) => !level.name.trim())) return 'Every level needs a name';
  if (isThresholdBased(draft.metric)) {
    const thresholds = draft.levels.map((level) => Number(level.threshold));
    if (thresholds.some((t) => !(t > 0))) return 'Every level needs a threshold above zero';
    if (thresholds.some((t, i) => i > 0 && t <= thresholds[i - 1])) return 'Level thresholds must increase';
  }
  return null;
};

export const unitLabel = (metric: AchievementMetric, reportFieldName?: string) =>
  metric === 'report_field_total' ? reportFieldName ?? '' : METRICS[metric].unit;

export const formatThresholdList = (values: string[]) => {
  if (values.length <= 1) return values.join('');
  return `${values.slice(0, -1).join(', ')} or ${values[values.length - 1]}`;
};
